import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { AddPaymentMethodModal as AddPaymentMethodModalComponent } from 'components/add-payment-method-modal';
import useDialogModal from 'hooks/use-dialog-modal';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { toast } from 'utils/toast';

import { useScanAndPayContext } from './state/provider';
import { THScanAndPayView } from './th-scan-and-pay.view';
import { ScanAndPayViews } from './types';
import { getDefaultPaymentMethodId } from './utils';

export const THScanAndPayContainer = () => {
  const enableTHScanAndPay = useFlag(LaunchDarklyFlag.ENABLE_TH_SCAN_AND_PAY);
  const { formatMessage } = useIntl();
  const {
    nonSelectedPaymentMethods,
    scanAndPayContent,
    scanAndPayContentLoading,
    scanAndPayEnabledForUser,
    loadingSelectedPaymentMethod,
    loadingStatus,
    selectedPaymentMethod,
    updateScanAndPayStatus,
    updateSelectedScanAndPayPaymentMethod,
    updatingSelectedPaymentMethod,
    updatingStatus,
    userHasValidScanAndPayPaymentMethodOption,
  } = useScanAndPayContext();

  const isLoading = useMemo(() => {
    return scanAndPayContentLoading || loadingStatus;
  }, [scanAndPayContentLoading, loadingStatus]);

  const isUpdating = useMemo(() => {
    return loadingSelectedPaymentMethod || updatingSelectedPaymentMethod || updatingStatus;
  }, [loadingSelectedPaymentMethod, updatingSelectedPaymentMethod, updatingStatus]);

  // if we're in process of toggling, optimistically assume that it
  // will work and toggle accordingly so user doesn't get click again
  const toggleChecked = updatingStatus ? !scanAndPayEnabledForUser : scanAndPayEnabledForUser;

  const [AddPaymentMethodModal, openAddPaymentMethodModal, , onDismiss] = useDialogModal<
    {},
    { onCardAdded: () => void; onDismiss: () => void }
  >({
    Component: AddPaymentMethodModalComponent,
  });

  const currentView = useMemo(() => {
    if (scanAndPayEnabledForUser) {
      return ScanAndPayViews.ACTIVE;
    }
    if (userHasValidScanAndPayPaymentMethodOption) {
      return ScanAndPayViews.INACTIVE;
    }
    return ScanAndPayViews.NO_PAYMENT_METHOD;
  }, [scanAndPayEnabledForUser, userHasValidScanAndPayPaymentMethodOption]);

  const handleToggleScanAndPay = useCallback(() => {
    // do not allow toggling when we are in process of checking the current state
    if (isLoading || isUpdating) {
      return;
    }
    // if it's currently on then we're turning it off
    // and don't need to pass defaultPaymentMethodId
    // if it's currently off then we're turning it on
    // and do need to pass a defaultPaymentMethodId
    const defaultPaymentMethodId = scanAndPayEnabledForUser
      ? ''
      : getDefaultPaymentMethodId(nonSelectedPaymentMethods);
    updateScanAndPayStatus(!scanAndPayEnabledForUser, defaultPaymentMethodId);
  }, [
    isLoading,
    isUpdating,
    scanAndPayEnabledForUser,
    nonSelectedPaymentMethods,
    updateScanAndPayStatus,
  ]);

  if (!enableTHScanAndPay) {
    return null;
  }

  return (
    <>
      <THScanAndPayView
        addPaymentMethodButtonText={formatMessage({ id: 'addPaymentMethod' })}
        currentView={currentView}
        isLoading={isLoading}
        isUpdating={isUpdating}
        nonSelectedPaymentMethods={nonSelectedPaymentMethods}
        openAddPaymentMethodModal={openAddPaymentMethodModal}
        scanAndPayTextContent={scanAndPayContent}
        selectedPaymentMethod={selectedPaymentMethod}
        toggleChecked={toggleChecked}
        toggleScanAndPay={handleToggleScanAndPay}
        updateScanAndPayPaymentMethod={updateSelectedScanAndPayPaymentMethod}
      />
      <AddPaymentMethodModal
        onCardAdded={() => toast.success(formatMessage({ id: 'paymentMethodAdded' }))}
        onDismiss={onDismiss}
      />
    </>
  );
};
